import { useCallback, useContext, useMemo } from 'react';
import { isNil } from 'lodash';
import { ANALYST_VALUE, FIRM_ADMIN_VALUE, FUND_ADMIN_VALUE } from 'common/constants/user';
import { useStore } from 'common/store';
import { DialogContextValues } from 'common/types/dialogContext';
import { UseStoreValues } from 'common/types/store';
import { ConfirmationDialog } from 'components/Dialogs';
import { DialogContext } from 'context';
import {
  READY_FOR_AUDIT,
  REMOVE_READY_FOR_AUDIT,
  SET_AS_READY_FOR_AUDIT,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { SetReadyForAuditConfirmation } from 'pages/ValuationsAllocation/components';
import { useSetReadyForAudit } from 'services/hooks/valuations';
import { SetReadyForAuditParams } from 'services/hooks/valuations/useSetReadyForAudit';
import { getBooleanValue, getNumberValue, getObjectValue, handleCatchError } from 'utilities';
import {
  UseFundInvestmentValuationReadyForAuditParams,
  UseFundInvestmentValuationReadyForAuditReturn,
} from './useFundInvestmentValuationReadyForAudit.types';

const useFundInvestmentValuationReadyForAudit = (
  params: UseFundInvestmentValuationReadyForAuditParams
): UseFundInvestmentValuationReadyForAuditReturn => {
  const { getValuationByAllocation, valuationByAllocation } = params;
  const { id: valuationId, is_ready_for_audit: valuationIsReadyForAudit } = getObjectValue(valuationByAllocation);

  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo: currentFirm, user: currentUser } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);
  const {
    firms_permissions: currentUserPermissions,
    is_staff: isCurrentUserStaff = false,
    is_superuser: isCurrentUserSuperUser = false,
  } = getObjectValue(currentUser);

  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;

  const { mutateAsync: setReadyForAudit } = useSetReadyForAudit();

  const isCurrentValuationReadyForAudit = useMemo<boolean>(
    () => getBooleanValue(valuationIsReadyForAudit),
    [valuationIsReadyForAudit]
  );

  const isCurrentUserAllowedToToggleReadyForAudit = useMemo<boolean>(() => {
    if (!currentUserPermissions || !currentFirmId) {
      return false;
    }

    const fiirmPermission = currentUserPermissions.find(permission => permission.id === currentFirmId);

    if (!fiirmPermission) {
      return false;
    }

    const isRoleAuthorized = [FIRM_ADMIN_VALUE, FUND_ADMIN_VALUE, ANALYST_VALUE].includes(
      fiirmPermission.user_firm_role
    );

    return isCurrentUserSuperUser || isCurrentUserStaff || isRoleAuthorized;
  }, [currentFirmId, currentUserPermissions, isCurrentUserStaff, isCurrentUserSuperUser]);

  const valuationReadyForAuditLabel = useMemo<string>(
    () => (isCurrentValuationReadyForAudit ? REMOVE_READY_FOR_AUDIT : READY_FOR_AUDIT),
    [isCurrentValuationReadyForAudit]
  );

  const changeValuationReadyForAudit = useCallback(async () => {
    const valuationSetReadyForAudit = {
      valuationId: getNumberValue(valuationId),
      readyForAuditData: { is_ready_for_audit: !isCurrentValuationReadyForAudit },
    } as SetReadyForAuditParams;

    const updatedIsReadyForAudit = await setReadyForAudit(valuationSetReadyForAudit);

    if (!isNil(updatedIsReadyForAudit)) {
      getValuationByAllocation().catch(handleCatchError);
    }
  }, [getValuationByAllocation, isCurrentValuationReadyForAudit, setReadyForAudit, valuationId]);

  const showValuationReadyForAuditDialog = useCallback(() => {
    showDialog({
      wrapper: ConfirmationDialog,
      wrapperProps: {
        title: valuationReadyForAuditLabel,
      },
      content: () => <SetReadyForAuditConfirmation currentReadinessStatus={isCurrentValuationReadyForAudit} />,
      actions: [
        {
          callback: changeValuationReadyForAudit,
          label: isCurrentValuationReadyForAudit ? REMOVE_READY_FOR_AUDIT : SET_AS_READY_FOR_AUDIT,
          type: 'primary' as const,
        },
      ],
    });
  }, [changeValuationReadyForAudit, isCurrentValuationReadyForAudit, showDialog, valuationReadyForAuditLabel]);

  return {
    isCurrentUserAllowedToToggleReadyForAudit,
    isCurrentValuationReadyForAudit,
    showValuationReadyForAuditDialog,
    valuationReadyForAuditLabel,
  };
};

export default useFundInvestmentValuationReadyForAudit;
